import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel
} from '../components/ui/alert-dialog';
import { User, Building2, Save, Crown, ArrowUpRight, Loader2, Trash2, AlertTriangle, Bell, Send, Users, FileText, Check, Minus } from 'lucide-react';
import { toast } from 'sonner';
import { Switch } from '../components/ui/switch';

import { useNavigate } from 'react-router-dom';

const API = process.env.REACT_APP_BACKEND_URL;

const PLAN_DETAILS = {
  free: { name: 'Free', price: '$0', period: '/mo', employeesNum: 2, pafsNum: 2, color: 'bg-gray-100 text-gray-700 border-gray-200', icon: 'text-gray-500', badge: 'bg-gray-100 text-gray-600', ring: 'ring-gray-200' },
  starter: { name: 'Starter', price: '$15', period: '/mo', employeesNum: 15, pafsNum: 15, color: 'bg-indigo-50 text-indigo-700 border-indigo-200', icon: 'text-indigo-500', badge: 'bg-indigo-100 text-indigo-700', ring: 'ring-indigo-200' },
  professional: { name: 'Professional', price: '$25', period: '/mo', employeesNum: 25, pafsNum: 25, color: 'bg-violet-50 text-violet-700 border-violet-200', icon: 'text-violet-500', badge: 'bg-violet-100 text-violet-700', ring: 'ring-violet-200' },
  enterprise: { name: 'Enterprise', price: 'Custom', period: '', employeesNum: Infinity, pafsNum: Infinity, color: 'bg-amber-50 text-amber-700 border-amber-200', icon: 'text-amber-500', badge: 'bg-amber-100 text-amber-700', ring: 'ring-amber-200' },
};

const ALL_PLANS = ['free', 'starter', 'professional', 'enterprise'];
const PLAN_FEATURES = {
  free: ['Basic compliance tracking', 'Document uploads', 'Community support'],
  starter: ['Compliance automation', 'Email reminders', 'CSV exports'],
  professional: ['Priority support', 'Audit trail', 'Role-based access'],
  enterprise: ['Dedicated account manager', 'SLA guarantee', 'SSO & advanced security'],
};

export default function SettingsPage() {
  const { user, apiCall, checkAuth, token, logout } = useAuth();
  const navigate = useNavigate();
  const accountType = user?.account_type || 'starter';
  const plan = PLAN_DETAILS[accountType] || PLAN_DETAILS.starter;
  const [upgrading, setUpgrading] = useState(false);
  const [planUsage, setPlanUsage] = useState(null);
  const [subStatus, setSubStatus] = useState(null);
  const [cancelling, setCancelling] = useState(false);

  useEffect(() => {
    apiCall('get', '/plan/usage').then(res => setPlanUsage(res.data)).catch(() => {});
    apiCall('get', '/payments/subscription-status').then(res => setSubStatus(res.data)).catch(() => {});
  }, []);

  const nextPlan = accountType === 'free' ? 'starter' : accountType === 'starter' ? 'professional' : null;

  const handleUpgrade = async () => {
    if (!nextPlan || !token) return;
    setUpgrading(true);
    try {
      const res = await fetch(`${API}/payments/create-checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ plan_id: nextPlan, origin_url: window.location.origin }),
      });
      const data = await res.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        toast.error(data.detail || 'Failed to initiate upgrade');
      }
    } catch {
      toast.error('Error initiating upgrade. Please try again.');
    } finally {
      setUpgrading(false);
    }
  };

  const handleCancelSubscription = async () => {
    setCancelling(true);
    try {
      const res = await apiCall('post', '/payments/cancel-subscription');
      toast.success(res.data.message || 'Subscription will cancel at end of billing period');
      setSubStatus(prev => ({ ...prev, cancel_at_period_end: true }));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to cancel subscription');
    } finally {
      setCancelling(false);
    }
  };

  const handleBillingPortal = async () => {
    try {
      const res = await apiCall('post', '/payments/billing-portal');
      if (res.data.url) window.location.href = res.data.url;
    } catch {
      toast.error('Failed to open billing portal');
    }
  };

  const nameParts = (user?.name || '').split(' ');
  const [firstName, setFirstName] = useState(user?.first_name || nameParts[0] || '');
  const [lastName, setLastName] = useState(user?.last_name || nameParts.slice(1).join(' ') || '');
  const [companyName, setCompanyName] = useState(user?.company_name || '');
  const [companyEin, setCompanyEin] = useState(user?.company_ein || '');
  const [companyAddress, setCompanyAddress] = useState(user?.company_address || '');
  const [companyPhone, setCompanyPhone] = useState(user?.company_phone || '');
  const [companyWebsite, setCompanyWebsite] = useState(user?.company_website || '');
  const [saving, setSaving] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteEmail, setDeleteEmail] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [sendingReminders, setSendingReminders] = useState(false);

  const defaultPrefs = { lca_expiry_30d: true, lca_expiry_7d: true, lca_expired: true, missing_docs_weekly: true, paf_status_change: true, document_uploaded: true };
  const [notifPrefs, setNotifPrefs] = useState(user?.notification_preferences || defaultPrefs);

  const togglePref = (key) => {
    setNotifPrefs(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const handleSendReminders = async () => {
    setSendingReminders(true);
    try {
      const res = await apiCall('post', '/reminders/send-now');
      const data = res.data;
      if (data.emails_sent > 0) {
        toast.success(`${data.emails_sent} reminder(s) sent to your email`);
      } else {
        toast.info('No compliance issues found — no reminders to send');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send reminders');
    } finally {
      setSendingReminders(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await apiCall('put', '/settings', {
        first_name: firstName,
        last_name: lastName,
        company_name: companyName,
        company_ein: companyEin,
        company_address: companyAddress,
        company_phone: companyPhone,
        company_website: companyWebsite,
        notification_preferences: notifPrefs,
      });
      await checkAuth();
      toast.success('Settings saved');
    } catch (err) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="settings-page">
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
        <h1 className="text-3xl font-bold text-[#0F172A] tracking-tight mb-1">Settings</h1>
        <p className="text-[#64748B] mb-8">Manage your account and preferences</p>
      </motion.div>

      <div className="max-w-2xl space-y-6">
        {/* Profile */}
        <motion.div
          initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.05 }}
          className="bg-white rounded-2xl border border-indigo-100/50 p-6 md:p-8 shadow-sm"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-xl bg-[#EEF2FF] flex items-center justify-center">
              <User className="w-5 h-5 text-[#4F46E5]" strokeWidth={1.5} />
            </div>
            <div>
              <h2 className="text-base font-bold text-[#0F172A]">Profile</h2>
              <p className="text-xs text-[#64748B]">Your account information</p>
            </div>
          </div>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-medium text-[#0F172A] mb-1.5 block">First Name</Label>
                <Input
                  data-testid="settings-first-name"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder="John"
                  className="rounded-xl border-gray-200 bg-gray-50/50 h-11"
                />
              </div>
              <div>
                <Label className="text-sm font-medium text-[#0F172A] mb-1.5 block">Last Name</Label>
                <Input
                  data-testid="settings-last-name"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  placeholder="Doe"
                  className="rounded-xl border-gray-200 bg-gray-50/50 h-11"
                />
              </div>
            </div>
            <div>
              <Label className="text-sm font-medium text-[#0F172A] mb-1.5 block">Email</Label>
              <Input
                value={user?.email || ''}
                disabled
                className="rounded-xl border-gray-200 bg-gray-100 h-11 text-[#64748B]"
              />
            </div>
            <div>
              <Label className="text-sm font-medium text-[#0F172A] mb-1.5 block">Role</Label>
              <Input
                value={user?.role?.replace('_', ' ') || ''}
                disabled
                className="rounded-xl border-gray-200 bg-gray-100 h-11 text-[#64748B] capitalize"
              />
            </div>
          </div>
        </motion.div>

        {/* Account Type */}
        <motion.div
          initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.07 }}
          className="bg-white rounded-2xl border border-indigo-100/50 p-6 md:p-8 shadow-sm"
          data-testid="account-type-section"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-xl bg-[#F0EBFF] flex items-center justify-center">
              <Crown className={`w-5 h-5 ${plan.icon}`} strokeWidth={1.5} />
            </div>
            <div>
              <h2 className="text-base font-bold text-[#0F172A]">Account Type</h2>
              <p className="text-xs text-[#64748B]">Your current plan and usage</p>
            </div>
          </div>

          {/* Current Plan Header */}
          <div className="flex items-center justify-between p-4 rounded-xl border border-indigo-100/80 bg-[#FAFBFF] mb-5">
            <div className="flex items-center gap-4">
              <span className={`inline-flex items-center px-3 py-1.5 rounded-lg text-sm font-semibold border ${plan.color}`} data-testid="account-type-badge">
                {plan.name}
              </span>
              <div>
                <p className="text-sm font-semibold text-[#0F172A]" data-testid="account-type-price">{plan.price}<span className="text-xs font-normal text-[#64748B]">{plan.period}</span></p>
                <p className="text-xs text-[#64748B]">{plan.employeesNum === Infinity ? 'Unlimited' : plan.employeesNum} employees &middot; {plan.pafsNum === Infinity ? 'Unlimited' : plan.pafsNum} PAF files</p>
              </div>
            </div>
            {accountType !== 'enterprise' && nextPlan && (
              <Button
                data-testid="upgrade-plan-btn"
                onClick={handleUpgrade}
                disabled={upgrading}
                variant="outline"
                className="rounded-xl border-indigo-200 text-indigo-600 hover:bg-indigo-50 text-sm font-medium h-9 px-4"
              >
                {upgrading ? (
                  <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Processing...</>
                ) : (
                  <>Upgrade to {PLAN_DETAILS[nextPlan]?.name}<ArrowUpRight className="w-3.5 h-3.5 ml-1.5" /></>
                )}
              </Button>
            )}
            {accountType === 'enterprise' && (
              <span className="text-xs text-emerald-600 font-semibold bg-emerald-50 px-3 py-1.5 rounded-lg">Top Tier</span>
            )}
            {accountType === 'professional' && (
              <Button
                data-testid="upgrade-plan-btn"
                onClick={() => navigate('/contact')}
                variant="outline"
                className="rounded-xl border-indigo-200 text-indigo-600 hover:bg-indigo-50 text-sm font-medium h-9 px-4"
              >
                Contact for Enterprise
                <ArrowUpRight className="w-3.5 h-3.5 ml-1.5" />
              </Button>
            )}
          </div>

          {/* Usage Progress Bars */}
          {planUsage && (
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div className="p-4 rounded-xl border border-gray-100 bg-[#FAFBFF]" data-testid="employees-usage">
                <div className="flex items-center gap-2 mb-2">
                  <Users className="w-4 h-4 text-indigo-500" />
                  <span className="text-xs font-semibold text-[#0F172A]">Employees</span>
                </div>
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-lg font-bold text-[#0F172A]">{planUsage.employees_used}</span>
                  <span className="text-xs text-[#94A3B8]">of {planUsage.employees_limit === 999999 ? '∞' : planUsage.employees_limit}</span>
                </div>
                <div className="w-full h-2 rounded-full bg-gray-100 overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all duration-500 ${
                      planUsage.employees_limit !== 999999 && planUsage.employees_used / planUsage.employees_limit > 0.8
                        ? 'bg-red-500' : 'bg-indigo-500'
                    }`}
                    style={{ width: `${planUsage.employees_limit === 999999 ? 5 : Math.min(100, (planUsage.employees_used / planUsage.employees_limit) * 100)}%` }}
                  />
                </div>
              </div>
              <div className="p-4 rounded-xl border border-gray-100 bg-[#FAFBFF]" data-testid="pafs-usage">
                <div className="flex items-center gap-2 mb-2">
                  <FileText className="w-4 h-4 text-violet-500" />
                  <span className="text-xs font-semibold text-[#0F172A]">PAF Files</span>
                </div>
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-lg font-bold text-[#0F172A]">{planUsage.pafs_used}</span>
                  <span className="text-xs text-[#94A3B8]">of {planUsage.pafs_limit === 999999 ? '∞' : planUsage.pafs_limit}</span>
                </div>
                <div className="w-full h-2 rounded-full bg-gray-100 overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all duration-500 ${
                      planUsage.pafs_limit !== 999999 && planUsage.pafs_used / planUsage.pafs_limit > 0.8
                        ? 'bg-red-500' : 'bg-violet-500'
                    }`}
                    style={{ width: `${planUsage.pafs_limit === 999999 ? 5 : Math.min(100, (planUsage.pafs_used / planUsage.pafs_limit) * 100)}%` }}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Subscription Management */}
          {subStatus?.has_subscription && accountType !== 'free' && accountType !== 'enterprise' && (
            <div className="bg-[#F8FAFF] rounded-xl p-4 space-y-3" data-testid="subscription-management">
              <p className="text-xs font-semibold text-[#94A3B8] uppercase tracking-wider">Subscription</p>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-[#0F172A] font-medium">
                    {subStatus.status === 'active' ? 'Active' : subStatus.status}
                    {subStatus.cancel_at_period_end && <span className="text-amber-600 ml-2 text-xs">(Cancels at period end)</span>}
                  </p>
                  {subStatus.current_period_end && (
                    <p className="text-xs text-[#64748B] mt-0.5">
                      {subStatus.cancel_at_period_end ? 'Access until' : 'Next billing'}: {new Date(subStatus.current_period_end * 1000).toLocaleDateString()}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" className="rounded-lg text-xs" onClick={handleBillingPortal} data-testid="billing-portal-btn">
                    Manage Billing
                  </Button>
                  {!subStatus.cancel_at_period_end && (
                    <Button variant="outline" size="sm" className="rounded-lg text-xs text-red-600 border-red-200 hover:bg-red-50" onClick={handleCancelSubscription} disabled={cancelling} data-testid="cancel-subscription-btn">
                      {cancelling ? 'Cancelling...' : 'Cancel Plan'}
                    </Button>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* All Plans Comparison */}
          <div>
            <p className="text-xs font-semibold text-[#94A3B8] uppercase tracking-wider mb-3">All Plans</p>
            <div className="grid grid-cols-4 gap-3">
              {ALL_PLANS.map(planKey => {
                const p = PLAN_DETAILS[planKey];
                const isCurrent = planKey === accountType;
                const planIdx = ALL_PLANS.indexOf(planKey);
                const currentIdx = ALL_PLANS.indexOf(accountType);
                const isDowngrade = planIdx < currentIdx;
                return (
                  <div
                    key={planKey}
                    className={`relative p-4 rounded-xl border transition-all ${
                      isCurrent ? `border-2 ${p.ring} bg-white shadow-sm` : 'border-gray-100 bg-[#FAFBFF]'
                    }`}
                    data-testid={`plan-card-${planKey}`}
                  >
                    {isCurrent && (
                      <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 text-[10px] font-bold uppercase tracking-wider bg-indigo-600 text-white px-2.5 py-0.5 rounded-full">Current</span>
                    )}
                    <p className="text-sm font-bold text-[#0F172A] mb-0.5">{p.name}</p>
                    <p className="text-lg font-bold text-[#0F172A]">{p.price}<span className="text-xs font-normal text-[#64748B]">{p.period}</span></p>
                    <div className="mt-3 space-y-1.5">
                      <div className="flex items-center gap-1.5 text-xs text-[#475569]">
                        <Users className="w-3 h-3 text-[#94A3B8]" />
                        {p.employeesNum === Infinity ? 'Unlimited' : p.employeesNum} employees
                      </div>
                      <div className="flex items-center gap-1.5 text-xs text-[#475569]">
                        <FileText className="w-3 h-3 text-[#94A3B8]" />
                        {p.pafsNum === Infinity ? 'Unlimited' : p.pafsNum} PAF files
                      </div>
                      {PLAN_FEATURES[planKey]?.slice(0, 2).map((feat, i) => (
                        <div key={i} className="flex items-center gap-1.5 text-xs text-[#94A3B8]">
                          <Check className="w-3 h-3" />
                          {feat}
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </motion.div>

        {/* Company */}
        <motion.div
          initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.1 }}
          className="bg-white rounded-2xl border border-indigo-100/50 p-6 md:p-8 shadow-sm"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-xl bg-[#FFFBEB] flex items-center justify-center">
              <Building2 className="w-5 h-5 text-[#D97706]" strokeWidth={1.5} />
            </div>
            <div>
              <h2 className="text-base font-bold text-[#0F172A]">Company</h2>
              <p className="text-xs text-[#64748B]">Your organization details</p>
            </div>
          </div>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-medium text-[#0F172A] mb-1.5 block">Company Name</Label>
                <Input
                  data-testid="settings-company"
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  placeholder="Acme Corporation"
                  className="rounded-xl border-gray-200 bg-gray-50/50 h-11"
                />
              </div>
              <div>
                <Label className="text-sm font-medium text-[#0F172A] mb-1.5 block">EIN</Label>
                <Input
                  data-testid="settings-ein"
                  value={companyEin}
                  onChange={(e) => setCompanyEin(e.target.value)}
                  placeholder="XX-XXXXXXX"
                  className="rounded-xl border-gray-200 bg-gray-50/50 h-11"
                />
              </div>
            </div>
            <div>
              <Label className="text-sm font-medium text-[#0F172A] mb-1.5 block">Address</Label>
              <Input
                data-testid="settings-address"
                value={companyAddress}
                onChange={(e) => setCompanyAddress(e.target.value)}
                placeholder="123 Main St, Suite 100, City, State ZIP"
                className="rounded-xl border-gray-200 bg-gray-50/50 h-11"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-medium text-[#0F172A] mb-1.5 block">Phone</Label>
                <Input
                  data-testid="settings-phone"
                  value={companyPhone}
                  onChange={(e) => setCompanyPhone(e.target.value)}
                  placeholder="(555) 123-4567"
                  className="rounded-xl border-gray-200 bg-gray-50/50 h-11"
                />
              </div>
              <div>
                <Label className="text-sm font-medium text-[#0F172A] mb-1.5 block">Website</Label>
                <Input
                  data-testid="settings-website"
                  value={companyWebsite}
                  onChange={(e) => setCompanyWebsite(e.target.value)}
                  placeholder="https://www.example.com"
                  className="rounded-xl border-gray-200 bg-gray-50/50 h-11"
                />
              </div>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.15 }}
        >
          <Button
            data-testid="save-settings-btn"
            onClick={handleSave}
            disabled={saving}
            className="bg-[#4F46E5] hover:bg-[#4338CA] text-white rounded-full px-8 font-medium"
          >
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </motion.div>

        {/* Notification Preferences */}
        <motion.div
          initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.16 }}
          className="bg-white rounded-2xl border border-indigo-100/50 p-6 md:p-8 shadow-sm"
          data-testid="notification-preferences-section"
        >
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-[#FFF4E6] flex items-center justify-center">
                <Bell className="w-5 h-5 text-amber-500" strokeWidth={1.5} />
              </div>
              <div>
                <h2 className="text-base font-bold text-[#0F172A]">Email Notifications</h2>
                <p className="text-xs text-[#64748B]">Control which emails you and your team receive</p>
              </div>
            </div>
            <Button
              data-testid="send-reminders-btn"
              onClick={handleSendReminders}
              disabled={sendingReminders}
              variant="outline"
              className="rounded-xl border-indigo-200 text-indigo-600 hover:bg-indigo-50 text-sm font-medium h-9 px-4"
            >
              {sendingReminders ? (
                <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Sending...</>
              ) : (
                <><Send className="w-3.5 h-3.5 mr-1.5" /> Send Now</>
              )}
            </Button>
          </div>
          <div className="space-y-4">
            <p className="text-xs font-semibold text-[#94A3B8] uppercase tracking-wider mb-1">Real-time Notifications</p>
            {[
              { key: 'paf_status_change', label: 'PAF Status Change', desc: 'When a PAF compliance status changes (e.g., compliant to warning)', color: 'bg-violet-50 text-violet-600 border-violet-100' },
              { key: 'document_uploaded', label: 'Document Uploaded', desc: 'When a new document is uploaded to any PAF', color: 'bg-blue-50 text-blue-600 border-blue-100' },
            ].map(item => (
              <div key={item.key} className="flex items-center justify-between p-4 rounded-xl border border-gray-100 bg-[#FAFBFF] hover:bg-white transition-colors">
                <div className="flex items-center gap-3">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-semibold border ${item.color}`}>
                    {item.label}
                  </span>
                  <p className="text-xs text-[#64748B]">{item.desc}</p>
                </div>
                <Switch
                  data-testid={`toggle-${item.key}`}
                  checked={notifPrefs[item.key] ?? true}
                  onCheckedChange={() => togglePref(item.key)}
                />
              </div>
            ))}
            <p className="text-xs font-semibold text-[#94A3B8] uppercase tracking-wider mt-4 mb-1">Scheduled Reminders</p>
            {[
              { key: 'lca_expired', label: 'LCA Expired', desc: 'Immediate alert when an employee\'s LCA has expired', color: 'bg-red-50 text-red-600 border-red-100' },
              { key: 'lca_expiry_7d', label: 'Expiring in 7 Days', desc: 'Alert when an LCA expires within 7 days', color: 'bg-orange-50 text-orange-600 border-orange-100' },
              { key: 'lca_expiry_30d', label: 'Expiring in 30 Days', desc: 'Heads up when an LCA expires within 30 days', color: 'bg-amber-50 text-amber-600 border-amber-100' },
              { key: 'missing_docs_weekly', label: 'Missing Documents', desc: 'Weekly reminder for PAFs with incomplete document checklists', color: 'bg-indigo-50 text-indigo-600 border-indigo-100' },
            ].map(item => (
              <div key={item.key} className="flex items-center justify-between p-4 rounded-xl border border-gray-100 bg-[#FAFBFF] hover:bg-white transition-colors">
                <div className="flex items-center gap-3">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-semibold border ${item.color}`}>
                    {item.label}
                  </span>
                  <p className="text-xs text-[#64748B]">{item.desc}</p>
                </div>
                <Switch
                  data-testid={`toggle-${item.key}`}
                  checked={notifPrefs[item.key] ?? true}
                  onCheckedChange={() => togglePref(item.key)}
                />
              </div>
            ))}
          </div>
          <p className="mt-4 text-xs text-[#94A3B8]">Changes are saved when you click "Save Changes" above. Real-time notifications are sent instantly. Scheduled reminders run daily. All org members (Admin, HR, Employee) receive notifications based on their individual preferences.</p>
        </motion.div>

        {/* Account Management */}
        <motion.div
          initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.2 }}
          className="mt-10 bg-white rounded-2xl border border-red-200/60 p-6 md:p-8 shadow-sm"
          data-testid="danger-zone-section"
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-red-50 flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-red-500" strokeWidth={1.5} />
            </div>
            <div>
              <h2 className="text-base font-bold text-[#0F172A]">Account Management</h2>
              <p className="text-xs text-[#64748B]">Manage your account</p>
            </div>
          </div>

          {/* Delete Button */}
          <div className="flex items-center justify-between p-4 rounded-xl border border-red-100 bg-red-50/30">
            <div>
              <p className="text-sm font-semibold text-[#0F172A]">Delete Account</p>
              <p className="text-xs text-[#64748B]">Permanently delete your account and all associated data (employees, PAFs, documents)</p>
            </div>
            <Button
              data-testid="delete-account-btn"
              onClick={() => { setShowDeleteConfirm(true); setDeleteEmail(''); }}
              variant="outline"
              className="rounded-xl border-red-300 text-red-600 hover:bg-red-50 text-sm font-medium h-9 px-4"
            >
              <Trash2 className="w-3.5 h-3.5 mr-1.5" />
              Delete Account
            </Button>
          </div>

          {/* Delete Confirmation Dialog */}
          <AlertDialog open={showDeleteConfirm} onOpenChange={(open) => { setShowDeleteConfirm(open); if (!open) setDeleteEmail(''); }}>
            <AlertDialogContent className="rounded-2xl max-w-md">
              <AlertDialogHeader>
                <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-2">
                  <AlertTriangle className="w-6 h-6 text-red-600" />
                </div>
                <AlertDialogTitle className="text-center text-lg font-bold text-[#0F172A]">Delete your account?</AlertDialogTitle>
                <AlertDialogDescription className="text-center text-sm text-[#64748B]">
                  This action is permanent and cannot be undone. All your employees, PAF files, documents, and audit logs will be deleted forever.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <div className="py-2">
                <Label className="text-sm font-medium text-[#0F172A] mb-1.5 block">
                  Type <span className="font-bold text-red-600">{user?.email}</span> to confirm
                </Label>
                <Input
                  data-testid="delete-confirm-email"
                  value={deleteEmail}
                  onChange={(e) => setDeleteEmail(e.target.value)}
                  placeholder="Enter your email"
                  className="rounded-xl border-red-200 bg-white h-11 focus:ring-red-500/20 focus:border-red-400"
                />
              </div>
              <AlertDialogFooter className="flex gap-3 sm:gap-3">
                <AlertDialogCancel
                  data-testid="cancel-delete-btn"
                  className="rounded-xl h-10 px-5 text-sm flex-1"
                >
                  Cancel
                </AlertDialogCancel>
                <Button
                  data-testid="confirm-delete-btn"
                  onClick={async () => {
                    if (deleteEmail !== user?.email) {
                      toast.error('Email does not match your account');
                      return;
                    }
                    setDeleting(true);
                    try {
                      await apiCall('post', '/auth/delete-account', { confirm_email: deleteEmail });
                      toast.success('Account deleted');
                      logout();
                      navigate('/');
                    } catch (err) {
                      toast.error(err.response?.data?.detail || 'Failed to delete account');
                    } finally {
                      setDeleting(false);
                      setShowDeleteConfirm(false);
                    }
                  }}
                  disabled={deleting || deleteEmail !== user?.email}
                  className="bg-red-600 hover:bg-red-700 text-white rounded-xl h-10 px-5 text-sm font-semibold disabled:opacity-40 flex-1"
                >
                  {deleting ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Deleting...</>
                  ) : (
                    <><Trash2 className="w-4 h-4 mr-2" /> Delete Account</>
                  )}
                </Button>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </motion.div>
      </div>
    </div>
  );
}
